import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, CalendarDays, FileText, Sparkles, CloudRain, Calendar, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SCHEDULE_TYPE_META = {
  regular: { label: "Regular", icon: Calendar, description: "Weekly group schedule",
    activeCls: "border-sky-500 bg-sky-50 shadow-md", iconActive: "text-sky-700", labelActive: "text-sky-800" },
  special_event_friday: { label: "Special Event Friday", icon: Sparkles, description: "Friday / Color War / Final Week",
    activeCls: "border-fuchsia-500 bg-fuchsia-50 shadow-md", iconActive: "text-fuchsia-700", labelActive: "text-fuchsia-800" },
  rainy_day: { label: "Rainy Day", icon: CloudRain, description: "Indoor-only rainy-day plan",
    activeCls: "border-slate-500 bg-slate-100 shadow-md", iconActive: "text-slate-700", labelActive: "text-slate-800" },
  last_week: { label: "Last Week of Camp", icon: CalendarDays, description: "Per-day final-week plan",
    activeCls: "border-amber-500 bg-amber-50 shadow-md", iconActive: "text-amber-700", labelActive: "text-amber-800" },
};
const TYPE_ORDER = ["regular", "special_event_friday", "rainy_day", "last_week"];
const LAST_WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const DAY_LABEL = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri" };

// Deterministic pastel color per activity so the grid is readable at a glance.
function activityColor(name) {
  if (!name) return "";
  const palette = [
    "bg-sky-50 text-sky-900 border-sky-200",
    "bg-emerald-50 text-emerald-900 border-emerald-200",
    "bg-amber-50 text-amber-900 border-amber-200",
    "bg-rose-50 text-rose-900 border-rose-200",
    "bg-violet-50 text-violet-900 border-violet-200",
    "bg-teal-50 text-teal-900 border-teal-200",
    "bg-orange-50 text-orange-900 border-orange-200",
    "bg-indigo-50 text-indigo-900 border-indigo-200",
    "bg-lime-50 text-lime-900 border-lime-200",
    "bg-fuchsia-50 text-fuchsia-900 border-fuchsia-200",
    "bg-cyan-50 text-cyan-900 border-cyan-200",
    "bg-yellow-50 text-yellow-900 border-yellow-200",
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function buildSpans(rows, dayCount) {
  const spans = rows.map(() => new Array(dayCount).fill(null));
  for (let c = 0; c < dayCount; c++) {
    let r = 0;
    while (r < rows.length) {
      const v = rows[r].activities[c] || "";
      if (!v) { spans[r][c] = { span: 1, value: "" }; r += 1; continue; }
      let end = r + 1;
      while (end < rows.length && (rows[end].activities[c] || "") === v) end += 1;
      spans[r][c] = { span: end - r, value: v };
      for (let k = r + 1; k < end; k++) spans[k][c] = { hidden: true };
      r = end;
    }
  }
  return spans;
}

function rainyEntriesFor(tab, group) {
  const row = (tab.rows || []).find(r => r.group === group);
  if (!row) return [];
  const H = tab.time_headers || [];
  const entries = [];
  let i = 0;
  for (const c of row.cells) {
    if (c.text) {
      const start = (H[i] || "").split("-")[0] || "";
      const end = (H[i + c.span - 1] || "").split("-")[1] || "";
      entries.push({ time: start && end ? `${start}-${end}` : H[i] || "", text: c.text });
    }
    i += c.span;
  }
  return entries;
}

function RainyGroupDayList({ tabs, group }) {
  return (
    <div className="space-y-4" data-testid="rainy-my-day">
      {tabs.map((t) => {
        const entries = rainyEntriesFor(t, (group || "").toUpperCase());
        return (
          <div key={t.id || t.tab_name}>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.tab_name}</p>
            {entries.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Nothing scheduled.</p>
            ) : (
              <div className="space-y-1.5">
                {entries.map((en, i) => (
                  <div key={i} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${activityColor(en.text)}`}
                    data-testid="rainy-group-entry">
                    {en.time ? (
                      <span className="font-mono text-[11px] leading-tight break-words w-[92px] shrink-0 overflow-hidden">{en.time}</span>
                    ) : null}
                    <span className="text-sm font-medium leading-tight whitespace-pre-line min-w-0">{en.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RainyDayGridTable({ tab, highlightGroup, onRowClick }) {
  const hg = (highlightGroup || "").toUpperCase();
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-[10px] sm:text-xs w-full" data-testid="rainy-grid-table">
        <thead>
          <tr className="bg-slate-100">
            <th className="border px-2 py-1.5 text-left font-semibold sticky left-0 bg-slate-100 z-10">Group</th>
            {tab.time_headers.map((h, i) => (
              <th key={i} className="border px-1.5 py-1.5 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tab.rows.map((row) => {
            const hl = hg && row.group === hg;
            return (
              <tr
                key={row.group}
                className={`cursor-pointer hover:bg-sky-50 active:bg-sky-100 ${hl ? "bg-yellow-50" : ""}`}
                onClick={() => onRowClick && onRowClick(row.group)}
                data-testid={`rainy-grid-row-${row.group}`}
              >
                <td className={`border px-2 py-1 font-bold whitespace-nowrap sticky left-0 z-10 ${hl ? "bg-yellow-200 text-yellow-900" : "bg-white"}`}>{row.group}</td>
                {row.cells.map((c, i) => (
                  <td key={i} colSpan={c.span} className={`border px-1.5 py-1 text-center leading-tight ${c.text ? activityColor(c.text) : hl ? "bg-yellow-50" : "bg-white"}`}>
                    {c.text ? <span className="font-medium whitespace-pre-line">{c.text}</span> : <span className="text-slate-300">—</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleGrid({ schedule }) {
  const rows = schedule?.rows || [];
  const days = schedule?.day_headers || [];
  const spans = useMemo(() => (rows.length && days.length ? buildSpans(rows, days.length) : []), [rows, days]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[11px] sm:text-xs" data-testid="schedule-grid">
        <thead>
          <tr className="bg-slate-100 sticky top-0">
            <th className="border px-2 py-2 text-left font-semibold w-[90px] sm:w-[110px]">Time</th>
            {days.map((d, i) => (
              <th key={i} className="border px-2 py-2 text-left font-semibold">{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="align-top">
              <td className="border px-2 py-1 text-muted-foreground whitespace-nowrap font-mono text-[10px] sm:text-xs">{row.time}</td>
              {days.map((_, c) => {
                const cell = spans[r]?.[c];
                if (!cell || cell.hidden) return null;
                const val = cell.value || "";
                const cls = val ? activityColor(val) : "bg-white";
                return (
                  <td key={c} rowSpan={cell.span} className={`border px-2 py-1 ${cls}`}>
                    {val ? <span className="font-medium leading-tight">{val}</span> : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ScheduleViewer({ groupCode, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState("regular");
  const [activeDow, setActiveDow] = useState("monday");
  const [autoInfo, setAutoInfo] = useState(null); // {today_iso, today_day_of_week, matched_priority}
  // Pop Up Storm PDF — independent of the XLSX grid types.
  const [stormStatus, setStormStatus] = useState({ exists: false });
  const [stormPdfUrl, setStormPdfUrl] = useState(null);
  const [stormOpen, setStormOpen] = useState(false);
  // Camp-wide grids (rainy day / special event Friday / last week days) —
  // replace the per-group view for a type when uploaded.
  const [gridsByType, setGridsByType] = useState({});
  const [rainTab, setRainTab] = useState(0);
  const [rainGroup, setRainGroup] = useState(null); // tapped group → rendered day schedule
  // Group logins (head counselor / assistant) see their own rendered line by
  // default; admins & supervisors see the whole camp grid.
  const { user } = useAuth();
  const isGroupLogin = ["counselor", "assistant"].includes(user?.user_type);
  const [showFullGrid, setShowFullGrid] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!groupCode) return;
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        // Load full variant set + auto-active in parallel.
        const [allRes, activeRes] = await Promise.allSettled([
          axios.get(`${API}/schedules/${encodeURIComponent(groupCode)}`, { headers }),
          axios.get(`${API}/schedules/${encodeURIComponent(groupCode)}/active`, { headers }),
        ]);

        let payload = null;
        if (allRes.status === "fulfilled") {
          payload = allRes.value.data;
        } else if (allRes.reason?.response?.status === 404) {
          // Even with no full variant set, /active might return — fall through to error path.
        }

        // Build a {types, lastWeekByDay} structure. The existing /api/schedules/{code}
        // endpoint groups all variants by schedule_type — for last_week we may have up
        // to 4 docs under that one bucket (one per day). The legacy code overwrote on
        // each iteration, so we walk the raw docs and bucket by day_of_week.
        const types = payload?.types || {};
        const lastWeekByDay = {};
        // The list endpoint returns at most 1 doc per (group, type) — but for last_week
        // there can be multiple. Server's /schedules/{code} flattens them; pick out the
        // raw doc for last_week and pull its day_of_week. To get all 4 days we need a
        // dedicated fetch — fall back to whatever the active endpoint returns when the
        // user clicks a day-of-week sub-tab (handled below).
        if (types.last_week && types.last_week.day_of_week) {
          lastWeekByDay[types.last_week.day_of_week] = types.last_week;
        }

        // Auto-select tab based on /active. Counselor can still tap any tab to override.
        if (activeRes.status === "fulfilled") {
          const a = activeRes.value.data;
          setAutoInfo({
            today_iso: a.today_iso,
            today_day_of_week: a.today_day_of_week,
            matched_priority: a.matched_priority,
          });
          if (a.active_type === "last_week") {
            setActiveType("last_week");
            if (a.day_of_week && LAST_WEEK_DAYS.includes(a.day_of_week)) setActiveDow(a.day_of_week);
            // Seed the day's schedule into our local bucket.
            if (a.schedule && a.day_of_week) lastWeekByDay[a.day_of_week] = a.schedule;
          } else if (TYPE_ORDER.includes(a.active_type)) {
            setActiveType(a.active_type);
          }
        } else if (payload) {
          // Fallback: pick first available type.
          const available = Object.keys(types || {});
          const pick = TYPE_ORDER.find(t => available.includes(t)) || available[0] || "regular";
          setActiveType(pick);
        }

        setData(payload ? { ...payload, lastWeekByDay } : { types: {}, lastWeekByDay });
        setError(payload ? null : "No schedule has been uploaded for this group yet.");
      } catch {
        setError("Failed to load schedule.");
        toast.error("Failed to load schedule");
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [groupCode]);

  // Pop Up Storm status — independent of the per-group XLSX schedule.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/schedules/pop-up-storm/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStormStatus(res.data || { exists: false });
      } catch { /* silent — banner just stays hidden */ }
      try {
        const token = localStorage.getItem("token");
        const gridRes = await axios.get(`${API}/schedules/grid`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setGridsByType(gridRes.data?.grids || {});
      } catch { /* silent — falls back to per-group views */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const openStormPdf = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/schedules/pop-up-storm/file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setStormPdfUrl(url);
      setStormOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Pop Up Storm PDF");
    }
  };

  const closeStormPdf = () => {
    setStormOpen(false);
    if (stormPdfUrl) { URL.revokeObjectURL(stormPdfUrl); setStormPdfUrl(null); }
  };

  useEffect(() => () => {
    if (stormPdfUrl) URL.revokeObjectURL(stormPdfUrl);
  }, [stormPdfUrl]);

  const types = data?.types || {};
  const lastWeekByDay = data?.lastWeekByDay || {};
  const active = activeType === "last_week" ? lastWeekByDay[activeDow] : types[activeType];
  const activeGridKey = activeType === "last_week" ? `last_week_${activeDow}` : activeType;
  const activeGrid = (gridsByType[activeGridKey] || []).length > 0 ? gridsByType[activeGridKey] : null;
  const ActiveGridIcon = SCHEDULE_TYPE_META[activeType]?.icon || CloudRain;
  useEffect(() => { setRainTab(0); }, [activeGridKey]);

  return (
    <div className="min-h-screen bg-background p-3 sm:p-6" data-testid="schedule-viewer">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button variant="ghost" onClick={onBack} data-testid="schedule-viewer-back" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            <h1 className="text-lg sm:text-2xl font-bold font-heading">Group Schedule — {groupCode}</h1>
          </div>
          <div className="w-16" />
        </div>

        {loading ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">Loading schedule…</CardContent></Card>
        ) : error ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-10 text-center">
              <FileText className="w-8 h-8 text-amber-600 mx-auto mb-3" />
              <p className="font-medium text-amber-900" data-testid="schedule-viewer-error">{error}</p>
              <p className="text-sm text-amber-800 mt-1">Ask your admin to upload the schedule file.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {autoInfo && autoInfo.matched_priority === "primary" && activeType !== "regular" && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="schedule-auto-banner">
                Today is <strong>{autoInfo.today_day_of_week}</strong> ({autoInfo.today_iso}) — auto-showing <strong>{SCHEDULE_TYPE_META[activeType].label}</strong>. Tap any tab to override.
              </div>
            )}

            {stormStatus?.exists && (
              <div className="mb-3 rounded-lg border-2 border-indigo-200 bg-indigo-50 px-3 py-2 flex items-center justify-between gap-3"
                data-testid="schedule-pop-up-storm-banner">
                <div className="flex items-center gap-2 text-sm text-indigo-900 min-w-0">
                  <CloudRain className="w-4 h-4 flex-shrink-0" />
                  <span className="font-semibold">Pop Up Storm schedule available</span>
                  <span className="text-xs text-indigo-700 truncate hidden sm:inline">— {stormStatus.filename}</span>
                </div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={openStormPdf} data-testid="schedule-pop-up-storm-view-btn">
                  View PDF
                </Button>
              </div>
            )}

            {/* Type selector */}
            <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="schedule-type-tabs">
              {TYPE_ORDER.map(t => {
                const meta = SCHEDULE_TYPE_META[t];
                const Icon = meta.icon;
                const available = t === "last_week"
                  ? Object.keys(lastWeekByDay).length > 0 || LAST_WEEK_DAYS.some(d => (gridsByType[`last_week_${d}`] || []).length > 0)
                  : Boolean(types[t]) || (gridsByType[t] || []).length > 0;
                const isActive = activeType === t;
                return (
                  <button
                    key={t}
                    onClick={() => setActiveType(t)}
                    data-testid={`schedule-type-${t}`}
                    className={
                      "flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-center transition-all " +
                      (isActive
                        ? meta.activeCls
                        : available
                          ? "border-slate-200 bg-white hover:border-slate-300"
                          : "border-dashed border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300")
                    }
                  >
                    <Icon className={`w-5 h-5 ${isActive ? meta.iconActive : available ? "text-slate-600" : "text-slate-400"}`} />
                    <span className={`text-xs font-bold ${isActive ? meta.labelActive : available ? "text-slate-800" : "text-slate-500"}`}>{meta.label}</span>
                    <span className="text-[10px] text-muted-foreground hidden sm:block">{available ? meta.description : "Not uploaded yet"}</span>
                  </button>
                );
              })}
            </div>

            {activeType === "last_week" && (
              <div className="mb-4 grid grid-cols-4 gap-2" data-testid="schedule-last-week-day-tabs">
                {LAST_WEEK_DAYS.map(d => {
                  const has = Boolean(lastWeekByDay[d]) || (gridsByType[`last_week_${d}`] || []).length > 0;
                  const isActive = activeDow === d;
                  return (
                    <button
                      key={d}
                      onClick={() => setActiveDow(d)}
                      data-testid={`schedule-last-week-day-${d}`}
                      className={
                        "rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all " +
                        (isActive
                          ? "border-amber-500 bg-amber-50 text-amber-900"
                          : has
                            ? "border-slate-200 bg-white text-slate-800 hover:border-slate-300"
                            : "border-dashed border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300")
                      }
                    >
                      {DAY_LABEL[d]}
                      {!has && <span className="block text-[9px] text-slate-400 font-normal">empty</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {activeType !== "regular" && activeGrid ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2 flex-wrap">
                    <ActiveGridIcon className="w-5 h-5 text-slate-600" />
                    <span>{activeGrid[rainTab]?.title || `${SCHEDULE_TYPE_META[activeType].label} Schedule`}</span>
                    <Badge variant="secondary" data-testid="schedule-group-badge">{groupCode}</Badge>
                  </CardTitle>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      {isGroupLogin && !showFullGrid
                        ? "Your group's full-day plan."
                        : "Your group's row is highlighted. Tap any group's row for its full-day schedule."}
                    </span>
                    {activeGrid[rainTab]?.uploaded_at && (
                      <span>Uploaded {new Date(activeGrid[rainTab].uploaded_at).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="rainy-grid-tabs">
                    {(!isGroupLogin || showFullGrid) && activeGrid.map((t, i) => (
                      <button
                        key={t.id || i}
                        onClick={() => setRainTab(i)}
                        data-testid={`rainy-grid-tab-${i}`}
                        className={
                          "rounded-lg border-2 px-4 py-1.5 text-xs font-bold transition-all " +
                          (rainTab === i
                            ? "border-slate-600 bg-slate-700 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
                        }
                      >
                        {t.tab_name}
                      </button>
                    ))}
                    {isGroupLogin && (
                      <button
                        onClick={() => setShowFullGrid(v => !v)}
                        data-testid="rainy-toggle-full-grid"
                        className="rounded-lg border-2 border-sky-300 bg-sky-50 text-sky-800 px-3 py-1.5 text-xs font-bold hover:border-sky-400 transition-all"
                      >
                        {showFullGrid ? "← Back to my schedule" : "View full camp grid"}
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className={isGroupLogin && !showFullGrid ? "p-4" : "p-0 sm:p-4"}>
                  {isGroupLogin && !showFullGrid ? (
                    <RainyGroupDayList tabs={activeGrid} group={groupCode} />
                  ) : (
                    activeGrid[rainTab] && (
                      <RainyDayGridTable tab={activeGrid[rainTab]} highlightGroup={groupCode} onRowClick={setRainGroup} />
                    )
                  )}
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2 flex-wrap">
                  <span>{active?.title || "Rolling River Day Camp"}</span>
                  <Badge variant="secondary" data-testid="schedule-group-badge">{groupCode}</Badge>
                  <Badge className="bg-slate-100 text-slate-700 border-slate-200" data-testid="schedule-active-type">
                    {SCHEDULE_TYPE_META[activeType].label}
                    {activeType === "last_week" ? ` · ${DAY_LABEL[activeDow]}` : ""}
                  </Badge>
                </CardTitle>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  {active?.notes && <span><strong>Notes:</strong> {active.notes}</span>}
                  {active?.date_label && <span>{active.date_label}</span>}
                  {active?.uploaded_at && (
                    <span>Uploaded {new Date(active.uploaded_at).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-4">
                {active ? (
                  <ScheduleGrid schedule={active} />
                ) : (
                  <div className="py-10 text-center text-sm text-muted-foreground" data-testid="schedule-empty-placeholder">
                    No schedule uploaded yet{activeType === "last_week" ? ` for ${DAY_LABEL[activeDow]}` : ""}.
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </>
        )}
      </div>

      {/* Tapped group → rendered full-day rainy schedule */}
      {rainGroup && activeGrid && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3"
          onClick={() => setRainGroup(null)} data-testid="rainy-group-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <CloudRain className="w-4 h-4 text-slate-600" />
                <span className="font-bold text-slate-800">{SCHEDULE_TYPE_META[activeType].label} — {rainGroup}</span>
              </div>
              <button onClick={() => setRainGroup(null)} className="p-1 rounded-full hover:bg-slate-100"
                data-testid="rainy-group-modal-close">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <div className="p-4">
              <RainyGroupDayList tabs={activeGrid} group={rainGroup} />
            </div>
          </div>
        </div>
      )}

      {/* Pop Up Storm — fullscreen PDF viewer */}
      {stormOpen && stormPdfUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col"
          data-testid="schedule-pop-up-storm-viewer">
          <div className="bg-white border-b px-3 py-2 flex items-center justify-between">
            <div className="font-semibold text-sm text-indigo-800 truncate">
              <CloudRain className="inline w-4 h-4 mr-1" />
              Pop Up Storm — {stormStatus?.filename}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" asChild>
                <a href={stormPdfUrl} download={stormStatus?.filename || "Pop_Up_Storm.pdf"}
                  data-testid="schedule-pop-up-storm-download-link">
                  Download
                </a>
              </Button>
              <Button size="sm" variant="ghost" onClick={closeStormPdf}
                data-testid="schedule-pop-up-storm-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <iframe
            src={stormPdfUrl}
            title="Pop Up Storm PDF"
            className="flex-1 w-full bg-white"
            data-testid="schedule-pop-up-storm-iframe"
          />
        </div>
      )}
    </div>
  );
}
